import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Download, Gift, Share2, Trophy, Users } from "lucide-react";
import { toast } from "sonner";
import { API, api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const CHIP = {
  1: "bg-[#F5C451]/15 text-[#F5C451] border-[#F5C451]/40",
  2: "bg-white/10 text-ink border-white/25",
  3: "bg-[#CE8946]/15 text-[#CE8946] border-[#CE8946]/40",
};

const PILL = "inline-flex min-h-[32px] items-center gap-1.5 rounded-full border border-white/15 px-3 text-[11px] text-ink-soft transition-colors hover:border-brand/50 hover:text-brand";

// A public wall for the members who bring friends, read in whichever language the visitor uses.
export default function HallOfFame() {
  const { lang } = useI18n();
  const [hall, setHall] = useState(null);

  useEffect(() => {
    api.get("/hall", { params: { lang } })
      .then(({ data }) => setHall(data))
      .catch((e) => console.error("could not load the hall of fame", e));
  }, [lang]);

  const say = hall?.words || {};

  useEffect(() => {
    document.title = say.kicker ? `${say.kicker} · ChatMiLu` : "Hall of fame · ChatMiLu";
  }, [say.kicker]);

  if (!hall) return <div className="mx-auto max-w-4xl px-5 py-14" data-testid="hall-loading" />;

  const card = (month, rank) => `${API}/hall/card.jpg?month=${month}&rank=${rank}&lang=${lang}`;

  const share = async (month, rank, name) => {
    const url = card(month, rank);
    const words = `${name} — ${say.kicker} · chatmilu.com/hall-of-fame`;
    if (navigator.share) {
      try {
        await navigator.share({ title: say.kicker, text: words, url });
        return;
      } catch {
        // the sheet was dismissed
      }
    }
    try {
      await navigator.clipboard.writeText(`${words} ${url}`);
      toast.success(say.copied || "Copied");
    } catch {
      window.open(url, "_blank", "noopener");
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-5 py-14 sm:px-8" data-testid="hall-of-fame">
      <p className="text-[11px] font-semibold uppercase tracking-widest text-brand">{say.kicker}</p>
      <h1 className="mt-2 font-display text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">
        {say.title}
      </h1>
      <p className="mt-3 max-w-2xl text-base text-ink-soft">{say.intro}</p>

      <div className="mt-8 flex flex-wrap gap-8" data-testid="hall-totals">
        <p className="text-sm">
          <span className="block font-display text-4xl font-bold text-brand">
            {hall.friends_brought}
          </span>
          <span className="text-ink-soft">{say.t_friends}</span>
        </p>
        <p className="text-sm">
          <span className="block font-display text-4xl font-bold">{hall.people}</span>
          <span className="text-ink-soft">{say.t_people}</span>
        </p>
        <p className="text-sm">
          <span className="block font-display text-4xl font-bold">{hall.days_given}</span>
          <span className="text-ink-soft">{say.t_days}</span>
        </p>
      </div>

      {hall.months.length === 0 ? (
        <div className="mt-10 rounded-3xl border border-white/10 bg-panel p-8" data-testid="hall-empty">
          <p className="text-base font-semibold">{say.empty_title}</p>
          <p className="mt-1 text-sm text-ink-soft">{say.empty_line}</p>
        </div>
      ) : (
        <div className="mt-10 space-y-6">
          {hall.months.map((month) => (
            <section
              key={month.month}
              data-testid={`hall-month-${month.month}`}
              className="rounded-3xl border border-white/10 bg-panel p-6"
            >
              <p className="flex items-center gap-2 text-base font-semibold">
                <Trophy className="h-4 w-4 text-brand" /> {month.label || month.month}
              </p>
              <ul className="mt-4 space-y-3">
                {month.winners.map((one) => (
                  <li
                    key={`${month.month}-${one.rank}`}
                    data-testid={`hall-winner-${month.month}-${one.rank}`}
                    className="rounded-2xl border border-white/8 bg-page p-4"
                  >
                    <div className="flex items-center gap-3 text-sm">
                      <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-bold ${
                        CHIP[one.rank] || "border-white/12 text-ink-soft"}`}>
                        {one.rank}
                      </span>
                      <span className="truncate">
                        {one.name}
                        {one.rank === 1 ? <Crown className="ms-1.5 inline h-3.5 w-3.5 text-[#F5C451]" /> : null}
                      </span>
                      <span className="ms-auto shrink-0 tabular-nums text-ink-soft">
                        {one.friends} {say.f_friends} · {one.days} {say.f_days}
                      </span>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      <img
                        src={card(month.month, one.rank)}
                        alt={one.name}
                        loading="lazy"
                        data-testid={`hall-card-img-${month.month}-${one.rank}`}
                        className="h-24 w-auto rounded-xl border border-white/10"
                      />
                      <button
                        data-testid={`hall-card-share-${month.month}-${one.rank}`}
                        onClick={() => share(month.month, one.rank, one.name)}
                        className={PILL}
                      >
                        <Share2 className="h-3.5 w-3.5" /> {say.share}
                      </button>
                      <a
                        data-testid={`hall-card-download-${month.month}-${one.rank}`}
                        href={card(month.month, one.rank)}
                        download={`chatmilu-${month.month}-${one.rank}.jpg`}
                        className={PILL}
                      >
                        <Download className="h-3.5 w-3.5" /> {say.download}
                      </a>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      <div className="mt-10 flex flex-wrap gap-3">
        <Link
          to="/tour"
          data-testid="hall-watch-tour"
          className="inline-flex min-h-[46px] items-center gap-2 rounded-full bg-brand px-6 text-sm font-semibold text-white transition-colors hover:bg-brand-hover active:scale-95"
        >
          <Users className="h-4 w-4" /> {say.cta_tour}
        </Link>
        <Link
          to="/settings"
          data-testid="hall-my-board"
          className="inline-flex min-h-[46px] items-center gap-2 rounded-full border border-white/15 px-5 text-sm text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
        >
          <Gift className="h-4 w-4" /> {say.cta_link}
        </Link>
      </div>
    </div>
  );
}
